import React, { useEffect, useState, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./BeforeFooter.css";
import b1 from "./s1.png";
import b2 from "./s2.png";
import { useLanguage } from "../../context/LanguageContext";
import { getT } from "../../translations";

gsap.registerPlugin(ScrollTrigger);

function BeforeFooter() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const containerRef = useRef(null);
  const { lang } = useLanguage();
  const t = getT(lang);
  const controlsB1 = useAnimation();
  const controlsB2 = useAnimation();
  const [isMobile, setIsMobile] = useState(false);
  const [showFirst, setShowFirst] = useState(true);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto switch between images every 10s on mobile
  useEffect(() => {
    if (isMobile) {
      const interval = setInterval(() => {
        setShowFirst((prev) => !prev);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [isMobile]);

  const handleHoverB1 = () => {
    if (!isMobile) {
      controlsB1.start({ opacity: 1, scale: 1.05 });
      controlsB2.start({ opacity: 0.2, scale: 0.95 });
    }
  };

  const handleHoverB2 = () => {
    if (!isMobile) {
      controlsB2.start({ opacity: 1, scale: 1.05 });
      controlsB1.start({ opacity: 0.2, scale: 0.95 });
    }
  };

  const handleUnhover = () => {
    if (!isMobile) {
      controlsB1.start({ opacity: 1, scale: 1 });
      controlsB2.start({ opacity: 1, scale: 1 });
    }
  };

  useEffect(() => {
    const section = sectionRef.current;
    const heading = headingRef.current;
    const container = containerRef.current;
    if (!section || !heading) return;
    gsap.set([heading, container], { opacity: 0, y: 32 });
    const tl = gsap.timeline({
      scrollTrigger: { trigger: section, start: "top 82%", toggleActions: "play none none none" },
    });
    tl.to(heading, { opacity: 1, y: 0, duration: 0.6, ease: "back.out(1.2)" })
      .to(container, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }, "-=0.3");
    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []);

  return (
    <section ref={sectionRef} className="before-footer">
      <h1 ref={headingRef} className="before-footer-heading">{t("beforeFooter.heading")}</h1>

      <div ref={containerRef} className="image-container">
        {!isMobile ? (
          <>
            <motion.img
              src={b1}
              alt="B1"
              className="b1-img"
              animate={controlsB1}
              initial={{ opacity: 1 }}
              onHoverStart={handleHoverB1}
              onHoverEnd={handleUnhover}
            />
            <motion.img
              src={b2}
              alt="B2"
              className="b2-img"
              animate={controlsB2}
              initial={{ opacity: 1 }}
              onHoverStart={handleHoverB2}
              onHoverEnd={handleUnhover}
            />
          </>
        ) : (
          <>
            <motion.img
              src={b1}
              alt="B1"
              className="b1-mobile"
              animate={{ opacity: showFirst ? 1 : 0 }}
              transition={{ duration: 1.5 }}
            />
            <motion.img
              src={b2}
              alt="B2"
              className="b2-mobile"
              animate={{ opacity: showFirst ? 0 : 1 }}
              transition={{ duration: 1.5 }}
            />
          </>
        )}
      </div>
    </section>
  );
}

export default BeforeFooter;
